import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { XIcon, SparklesIcon, PlusIcon, Trash2Icon, CheckCircle2Icon, ClockIcon, MessageSquareIcon, BookOpenIcon } from 'lucide-react';
import { useBusiness, CATALOG_LABEL } from './BusinessContext';
type Rule = {
  id: string;
  text: string;
  active: boolean;
};
type Faq = {
  id: string;
  q: string;
  a: string;
};
interface CatalogInstructionsModalProps {
  onClose: () => void;
}
const RULE_SUGGESTIONS = ['Always confirm rental dates before quoting a price', 'Offer airport pickup for any booking over 3 days', 'Mention the security deposit on every quote', 'Suggest monthly rates when a customer asks for 20+ days'];
export function CatalogInstructionsModal({
  onClose
}: CatalogInstructionsModalProps) {
  const {
    hub
  } = useBusiness();
  const catalogName = CATALOG_LABEL[hub?.type ?? 'generic'] || 'Catalog';
  const shopName = hub?.name ?? 'your business';
  const [instructions, setInstructions] = useState(`You are the AI assistant for ${shopName}. Be warm, concise and helpful. Always answer in the customer's language. Highlight availability, pricing and delivery, and never promise anything we don't offer.`);
  const [tone, setTone] = useState('Friendly & Professional');
  const [rules, setRules] = useState<Rule[]>([{
    id: 'r1',
    text: 'Always confirm rental dates before sharing a final price.',
    active: true
  }, {
    id: 'r2',
    text: 'Offer free delivery within Dubai for bookings over 5 days.',
    active: true
  }]);
  const [ruleDraft, setRuleDraft] = useState('');
  const [faqs, setFaqs] = useState<Faq[]>([{
    id: 'f1',
    q: 'Do you require a security deposit?',
    a: 'Yes, a refundable deposit is held during the rental and released on return.'
  }]);
  const [faqQ, setFaqQ] = useState('');
  const [faqA, setFaqA] = useState('');
  const addRule = (text?: string) => {
    const value = (text ?? ruleDraft).trim();
    if (!value) return;
    setRules(prev => [...prev, {
      id: `r${Date.now()}`,
      text: value,
      active: true
    }]);
    if (!text) setRuleDraft('');
  };
  const toggleRule = (id: string) => setRules(prev => prev.map(r => r.id === id ? {
    ...r,
    active: !r.active
  } : r));
  const removeRule = (id: string) => setRules(prev => prev.filter(r => r.id !== id));
  const addFaq = () => {
    if (!faqQ.trim() || !faqA.trim()) return;
    setFaqs(prev => [...prev, {
      id: `f${Date.now()}`,
      q: faqQ.trim(),
      a: faqA.trim()
    }]);
    setFaqQ('');
    setFaqA('');
  };
  const removeFaq = (id: string) => setFaqs(prev => prev.filter(f => f.id !== id));
  const activeRules = rules.filter(r => r.active).length;
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6" data-id="element-1048">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} data-id="element-1049" />
      <motion.div initial={{
      opacity: 0,
      scale: 0.96,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} className="relative w-full max-w-3xl bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[92vh]" data-id="element-1050">
        {/* Header */}
        <div className="flex-shrink-0 px-6 py-4 border-b border-slate-100 flex items-center justify-between" data-id="element-1051">
          <div className="flex items-center gap-3" data-id="element-1052">
            <div className="w-10 h-10 rounded-xl bg-ai-purple/10 flex items-center justify-center" data-id="element-1053">
              <SparklesIcon className="w-5 h-5 text-ai-purple" data-id="element-1054" />
            </div>
            <div data-id="element-1055">
              <h2 className="text-lg font-display font-bold text-dark-navy" data-id="element-1056">
                {catalogName} AI Instructions
              </h2>
              <p className="text-sm text-slate-500" data-id="element-1057">
                How Talab represents your whole {catalogName.toLowerCase()} with
                customers.
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-full transition-colors" data-id="element-1058">
            <XIcon className="w-5 h-5" data-id="element-1059" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-8" data-id="element-1060">
          {/* General instructions */}
          <section data-id="element-1061">
            <div className="flex items-center gap-2.5 mb-3" data-id="element-1062">
              <BookOpenIcon className="w-4 h-4 text-primary-indigo" data-id="element-1063" />
              <h3 className="font-bold text-dark-navy" data-id="element-1064">General Instructions</h3>
            </div>
            <textarea rows={4} value={instructions} onChange={e => setInstructions(e.target.value)} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo text-sm resize-none" data-id="element-1065" />
            <div className="mt-3" data-id="element-1066">
              <label className="block text-xs font-medium text-slate-500 mb-1.5" data-id="element-1067">
                Tone of voice
              </label>
              <select value={tone} onChange={e => setTone(e.target.value)} className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-indigo/20" data-id="element-1068">
                {['Friendly & Professional', 'Concise & Direct', 'Warm & Conversational', 'Formal & Premium'].map(t => <option key={t} data-id="element-1069">{t}</option>)}
              </select>
            </div>
          </section>

          {/* Catalogue-wide rules */}
          <section data-id="element-1070">
            <div className="flex items-center justify-between mb-3" data-id="element-1071">
              <div className="flex items-center gap-2.5" data-id="element-1072">
                <SparklesIcon className="w-4 h-4 text-ai-purple" data-id="element-1073" />
                <h3 className="font-bold text-dark-navy" data-id="element-1074">Catalogue Rules</h3>
              </div>
              <span className="text-xs text-slate-500" data-id="element-1075">
                {activeRules} of {rules.length} active
              </span>
            </div>

            <div className="relative mb-3" data-id="element-1076">
              <textarea rows={2} value={ruleDraft} onChange={e => setRuleDraft(e.target.value)} onKeyDown={e => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                addRule();
              }
            }} placeholder='E.g. "Always mention insurance options on every quote."' className="w-full pl-4 pr-28 py-3 bg-purple-50/50 border border-purple-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-ai-purple/30 focus:border-ai-purple text-sm resize-none" data-id="element-1077" />
              <button onClick={() => addRule()} disabled={!ruleDraft.trim()} className="absolute right-2 bottom-2 px-3 py-1.5 bg-ai-purple text-white text-xs font-bold rounded-lg hover:bg-purple-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1" data-id="element-1078">
                <PlusIcon className="w-3.5 h-3.5" data-id="element-1079" /> Add Rule
              </button>
            </div>

            <div className="flex flex-wrap gap-2 mb-4" data-id="element-1080">
              {RULE_SUGGESTIONS.map(s => <button key={s} onClick={() => addRule(s)} className="px-3 py-1.5 bg-white border border-slate-200 text-slate-600 text-xs font-medium rounded-full hover:border-ai-purple hover:text-ai-purple hover:bg-purple-50/50 transition-colors flex items-center gap-1" data-id="element-1081">
                  <PlusIcon className="w-3 h-3" data-id="element-1082" /> {s}
                </button>)}
            </div>

            <div className="space-y-2" data-id="element-1083">
              {rules.map(r => <div key={r.id} className={`flex items-center gap-3 px-4 py-3 border rounded-xl group ${r.active ? 'border-slate-200 bg-white' : 'border-slate-100 bg-slate-50 opacity-60'}`} data-id="element-1084">
                  <p className="flex-1 text-sm text-slate-700" data-id="element-1085">{r.text}</p>
                  <button onClick={() => toggleRule(r.id)} title="Toggle status" className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md transition-colors ${r.active ? 'bg-green-50 text-green-700 hover:bg-green-100' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`} data-id="element-1086">
                    {r.active ? <>
                        <CheckCircle2Icon className="w-3 h-3" data-id="element-1087" /> Active
                      </> : <>
                        <ClockIcon className="w-3 h-3" data-id="element-1088" /> Paused
                      </>}
                  </button>
                  <button onClick={() => removeRule(r.id)} className="p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-md opacity-0 group-hover:opacity-100 transition-all" title="Remove rule" data-id="element-1089">
                    <Trash2Icon className="w-4 h-4" data-id="element-1090" />
                  </button>
                </div>)}
            </div>
          </section>

          {/* Catalogue-wide FAQs */}
          <section data-id="element-1091">
            <div className="flex items-center gap-2.5 mb-3" data-id="element-1092">
              <MessageSquareIcon className="w-4 h-4 text-electric-cyan" data-id="element-1093" />
              <h3 className="font-bold text-dark-navy" data-id="element-1094">
                Frequently Asked Questions
              </h3>
            </div>

            <div className="space-y-3 mb-4" data-id="element-1095">
              {faqs.map(f => <div key={f.id} className="border border-slate-200 rounded-xl p-4 group relative" data-id="element-1096">
                  <button onClick={() => removeFaq(f.id)} className="absolute top-3 right-3 p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-md opacity-0 group-hover:opacity-100 transition-all" title="Remove FAQ" data-id="element-1097">
                    <Trash2Icon className="w-4 h-4" data-id="element-1098" />
                  </button>
                  <p className="font-medium text-dark-navy text-sm pr-8" data-id="element-1099">
                    {f.q}
                  </p>
                  <p className="text-sm text-slate-500 mt-1" data-id="element-1100">{f.a}</p>
                </div>)}
            </div>

            <div className="border border-slate-200 rounded-xl p-4 space-y-3 bg-slate-50/50" data-id="element-1101">
              <input value={faqQ} onChange={e => setFaqQ(e.target.value)} placeholder="Question (e.g. Do you deliver to Abu Dhabi?)" className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-indigo/20" data-id="element-1102" />
              <textarea rows={2} value={faqA} onChange={e => setFaqA(e.target.value)} placeholder="Answer" className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-indigo/20 resize-none" data-id="element-1103" />
              <div className="flex justify-end" data-id="element-1104">
                <button onClick={addFaq} disabled={!faqQ.trim() || !faqA.trim()} className="px-4 py-2 bg-primary-indigo text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5" data-id="element-1105">
                  <PlusIcon className="w-4 h-4" data-id="element-1106" /> Add FAQ
                </button>
              </div>
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 px-6 py-4 border-t border-slate-100 flex justify-end gap-3" data-id="element-1107">
          <button onClick={onClose} className="px-4 py-2 text-slate-600 text-sm font-medium hover:bg-slate-50 rounded-lg" data-id="element-1108">
            Cancel
          </button>
          <button onClick={onClose} className="px-5 py-2 bg-primary-indigo text-white text-sm font-medium rounded-lg hover:bg-indigo-700 flex items-center gap-2" data-id="element-1109">
            <CheckCircle2Icon className="w-4 h-4" data-id="element-1110" /> Save Instructions
          </button>
        </div>
      </motion.div>
    </div>;
}